/* eslint-disable static-service/require-stories -- we have stories for the Markdown component */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";

import SimpleMarkdown from "@khanacademy/simple-markdown";
import Link from "@khanacademy/wonder-blocks-link";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {InitialRequestUrl, RequestInfo} from "@khan/shared-core";
import {
    getClassicUrl,
    getClassroomUrl,
    HostnameParts,
    isKhanAcademyUrl,
} from "@khan/urls";

import type {MarkdownRule} from "../types.ts";

// Output rule for handling links in the following format: [<content>](<href> <title>)
// Ex. [Khan Academy](https://www.khanacademy.org "Link to Khan Academy")
export const linkRule: Readonly<MarkdownRule> = {
    ...SimpleMarkdown.defaultRules.link,
    react: (node, output, state) => {
        // Since we want all links to open in a new tab, we convert the parsed
        // URL to an absolute URL so that target=_blank will work.
        const href = convertToAbsoluteUrl(
            SimpleMarkdown.sanitizeUrl(node.target),
        );
        // If the href fails sanitization or is a malformed URL, we'll just
        // output the contents instead of trying to make it a link.
        if (!href) {
            return output(node.content);
        }

        // If there is no content, we'll fallback to rendering
        // a generic "Link" text
        const linkTitle =
            (Array.isArray(node.content) && node.content.length === 0) ||
            !node.content
                ? t`Link`
                : output(node.content);
        return (
            <Link
                key={state.key}
                href={href}
                style={styles.link}
                // Set target="_blank" so that we always open links in a new
                // tab and set rel="noopener noreferrer" to prevent phishing
                // attacks.
                target="_blank"
                rel="noopener noreferrer"
                inline
            >
                <Body style={styles.linkBody}>{linkTitle}</Body>
            </Link>
        );
    },
};

export const convertToAbsoluteUrl = (
    href?: string | null,
): string | null | undefined => {
    if (!href) {
        return null;
    }

    try {
        const url = toAbsoluteUrl(href);
        const urlString = url.toString();
        // If we're in production, just navigate to the absolute URL.
        if (!RequestInfo.IS_DEV_SERVER) {
            return urlString;
        }

        // If this is an external URL, just navigate to the absolute URL.
        if (!isKhanAcademyUrl(urlString)) {
            return urlString;
        }

        const hostnameParts = HostnameParts.parse(url.hostname);
        const isLocalhostUrl = InitialRequestUrl.hostname === "localhost";
        if (isLocalhostUrl) {
            return urlString;
        }

        // For simplicity, we'll only handle maintaining dev navigation for KA
        // EN Classic links and KA Classroom links:
        // e.g. "https://www.khanacademy.org/math"
        // Do not match URLs in subdomains,
        // e.g. "https://support.khanacademy.org/"
        const isKhanClassicUrl = hostnameParts.lang === "www";
        const isKhanClassroomUrl = hostnameParts.lang === "classroom";

        if (isKhanClassicUrl) {
            return new URL(url.pathname, getClassicUrl()).toString();
        }

        if (isKhanClassroomUrl) {
            return new URL(url.pathname, getClassroomUrl()).toString();
        }

        // For everything else, we default to the absolute URL.
        return urlString;
    } catch (e: any) {
        return null;
    }
};

const styles = StyleSheet.create({
    link: {
        display: "inline",
    },
    linkBody: {
        display: "inline",
        font: "inherit",
    },
});

const toAbsoluteUrl = (href: string): URL => {
    try {
        return new URL(href);
    } catch (e: any) {
        return new URL(href, InitialRequestUrl.origin);
    }
};
